import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { debounce } from "es-toolkit/function"
import { default as useAbortController } from "@/hooks/useAbortController"
import { DateOnly } from "@/lib/date-only"
import { getServerDateOnly } from "@/lib/initial-time-zone"
import { SLOT_MAX, SLOT_MIN } from "@/lib/slot"
import {
  FilterByAgentUserNameOrGenderInput,
  FilterByDateInput,
  GetAvailableSlotsCapacityInput,
  useAvailableSlotsCapacityQuery$data,
} from "@/modules/patients/services/__generated__/useAvailableSlotsCapacityQuery.graphql"
import { AvailableDates, fetchAvailableDatesCapacity } from "@/modules/patients/services/useAvailableDatesCapacityQuery"
import { fetchAvailableSlotsCapacityQuery } from "@/modules/patients/services/useAvailableSlotsCapacityQuery"

export type AvailableCapacitiesInput = GetAvailableSlotsCapacityInput
export type AgentUserNameOrGenderInput = FilterByAgentUserNameOrGenderInput

const buildDateFilter = (selectedDate: DateOnly): FilterByDateInput | null => {
  if (selectedDate.equals(getServerDateOnly())) {
    return null
  }

  const dateValue = selectedDate.toString()
  return { from: { date: dateValue, slotId: SLOT_MIN }, to: { date: dateValue, slotId: SLOT_MAX } }
}

const hasAvailableSlots = (data: useAvailableSlotsCapacityQuery$data): boolean => {
  const { availableSlotsCapacity } = data
  if ("result" in availableSlotsCapacity && availableSlotsCapacity.isSuccess && availableSlotsCapacity.result) {
    return availableSlotsCapacity.result.some((slot) => slot.isAvailable || slot.allowPriorityBooking)
  }
  return false
}

type State = { data: useAvailableSlotsCapacityQuery$data | undefined; isLoading: boolean; hasAvailableSlot?: boolean }

/**
 * Hook that combines slots and dates capacity queries.
 * Fetches slots first, then fetches available dates if no slots are available.
 * This provides fallback behavior to show users when capacity might be available.
 */
export const useFetchAvailableCapacities = (
  filters: GetAvailableSlotsCapacityInput,
  currentDate: DateOnly,
  isEmergency: boolean,
) => {
  const { abort, getSignal } = useAbortController()

  const [state, setState] = useState<State>({ data: undefined, isLoading: true })

  const availableDatesRef = useRef<AvailableDates | undefined>(undefined)
  const currentSelectedDateRef = useRef<DateOnly>(currentDate)
  const filtersRef = useRef(filters)

  const fetchSlotsWithFallback = useCallback(
    async (shouldKeepAvailableDates = false) => {
      // Cancel the previous request to prevent race conditions issues.
      abort()

      if (isEmergency) return

      // Reset value when filter changed
      if (!shouldKeepAvailableDates) {
        availableDatesRef.current = undefined
      }

      setState((prev) => (prev.isLoading ? prev : { ...prev, isLoading: true }))

      const input = filtersRef.current
      const dateInput = buildDateFilter(currentSelectedDateRef.current)

      const slotsData = await fetchAvailableSlotsCapacityQuery({ ...input, dateInput }, getSignal())

      let hasAvailableSlot = slotsData && hasAvailableSlots(slotsData)

      // In case initial request, should call next request to get available dates
      if (availableDatesRef.current === undefined) {
        const datesCapacity = await fetchAvailableDatesCapacity(input, getSignal())
        availableDatesRef.current = datesCapacity?.availableDatesCapacity
      }

      if (availableDatesRef.current) {
        hasAvailableSlot = availableDatesRef.current.isIrrelevant || availableDatesRef.current.availableDates.length > 0
      }

      setState((prev) => ({ ...prev, data: slotsData, isLoading: false, hasAvailableSlot }))
    },
    [abort, getSignal, isEmergency],
  )

  // should use debounce to prevent spam requests every time a filter is changed
  const debouncedFetch = useMemo(
    () => debounce((shouldKeepAvailableDates: boolean) => fetchSlotsWithFallback(shouldKeepAvailableDates), 500),
    [fetchSlotsWithFallback],
  )

  const debounceSearch = useCallback(
    (newSearchDate: DateOnly) => {
      currentSelectedDateRef.current = newSearchDate
      debouncedFetch(true)
    },
    [debouncedFetch],
  )

  useEffect(() => {
    filtersRef.current = filters
    debouncedFetch(false)
  }, [debouncedFetch, filters])

  return { availableDates: availableDatesRef.current, debounceSearch, ...state }
}
